import {WarningMessageAction} from '#/atoms/warning-message-action';
import {
  ActionMenuHeader,
  ActionMenuHeaderButton,
  ActionMenuHeaderIcons,
} from '#/components/actionmenu/action-menu-header';
import {ActionMenuLayoutContentNamed, ActionMenuLayoutFooter} from '#/components/actionmenu/action-menu-layout';
import {ActionMenuFormContainer} from '#/components/actionmenu/subcomponents';
import AsyncButton from '#/components/async-button/component';
import {WalletContext} from '#/contexts/wallet-provider';
import {useBlockingLoginAction} from '#/hooks/use-blocking-login-action';
import {useQueryNavigationActions} from '#/hooks/use-query-navigation-actions';
import {userFeedbackRequests} from '#/repository/fetch-user-feedback';
import {ExclamationTriangleIcon} from '@heroicons/react/24/outline';
import {FunctionComponent, useContext, useEffect, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {UnlistedLand} from 'shareland-common';

enum VOTE_STATE {
  INITIAL,
  LOADING,
  COMPLETED,
}

export const ActionMenuUnlistedLand: FunctionComponent<{unlistedLand: UnlistedLand}> = ({unlistedLand}) => {
  const {t} = useTranslation('translation');

  const {
    actionMenu: {close},
  } = useQueryNavigationActions();

  const {
    accountResult: {address: account},
  } = useContext(WalletContext);

  const [voteState, setVoteState] = useState(VOTE_STATE.INITIAL);

  const performVote = useBlockingLoginAction(async () => {
    if (!account) {
      return;
    }
    setVoteState(VOTE_STATE.LOADING);
    await userFeedbackRequests.unlistedLandCreate({account: account, land: unlistedLand});
    setVoteState(VOTE_STATE.COMPLETED);
  });

  useEffect(() => {
    setVoteState(VOTE_STATE.INITIAL);
  }, [unlistedLand]);

  return (
    <div className='flex flex-col items-stretch w-full h-full'>
      <ActionMenuHeader land={unlistedLand}>
        <ActionMenuHeaderButton onClick={close}>
          <ActionMenuHeaderIcons.Close />
        </ActionMenuHeaderButton>
      </ActionMenuHeader>

      <ActionMenuFormContainer onSubmit={performVote}>
        <ActionMenuLayoutContentNamed noticeMessages={[]}>
          {{
            grow: (
              <WarningMessageAction
                className='h-full'
                IconComponent={ExclamationTriangleIcon}
                iconStrokeClassName='stroke-yellowNotice'
                message={t('components.warningMessageAction.locationNotListed.message')}
              />
            ),
          }}
        </ActionMenuLayoutContentNamed>

        <ActionMenuLayoutFooter>
          <div className='w-full'>
            <AsyncButton
              className='py-2 mt-10 w-full'
              onClick={performVote}
              disabled={voteState !== VOTE_STATE.INITIAL}
              isLoading={voteState === VOTE_STATE.LOADING}
            >
              {voteState === VOTE_STATE.INITIAL
                ? t('pages.actionMenu.unlisted.vote.inactive')
                : t('pages.actionMenu.unlisted.vote.active')}
            </AsyncButton>
          </div>
        </ActionMenuLayoutFooter>
      </ActionMenuFormContainer>
    </div>
  );
};
